import type { CegoXmpp, XmppOnOpts, XmppOnResult } from '@cego/xmpp'
import type { ShallowRef } from 'vue'
import { useCoreLogger, useXmpp, onBeforeUnmount, onMounted, shallowRef } from '#imports'

// Return is awaited then discarded, so callbacks may return anything (e.g. a navigateTo result).
export type UseXmppListenerCallback<TMessage = XmppOnResult['json']> = (message: TMessage, result: XmppOnResult) => unknown | Promise<unknown>
export interface UseXmppListenerReturn<TMessage> {
    data: ShallowRef<TMessage | undefined>
}

export const useXmppListener = <TMessage = XmppOnResult['json']>(filters: XmppOnOpts, callback?: UseXmppListenerCallback<TMessage>): UseXmppListenerReturn<TMessage> => {
    if (import.meta.server) {
        return {
            data: shallowRef()
        }
    }

    let xmpp: InstanceType<typeof CegoXmpp> | null = null
    let listener: any = null
    let disposed = false

    const data = shallowRef<TMessage>()

    onMounted(() => {
        onEnabled().catch(error => useCoreLogger().withTag('useXmppListener').warn(error))
    })

    onBeforeUnmount(() => {
        disposed = true
        onDisabled()
    })

    function onEnabled() {
        const { ensureInit } = useXmpp()

        return ensureInit().then((instance) => {
            if (!instance || disposed) return

            xmpp = instance

            listener = xmpp.on(filters, async (result: XmppOnResult) => {
                const json = result.json as TMessage
                data.value = json

                if (callback) {
                    await callback(json, result)
                }
            })
        })
    }

    function onDisabled() {
        if (xmpp && listener) {
            xmpp.off(listener)

            listener = null
        }
    }

    return { data }
}
